import { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/shared/components/ui/tooltip";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

// Algoritmo Gregoriano Anônimo para cálculo da Páscoa
function getEaster(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function getHolidays(year: number): Record<string, string> {
  const holidays: Record<string, string> = {};

  // Feriados nacionais fixos
  const fixed: [number, number, string][] = [
    [1, 1, "Confraternização Universal"],
    [4, 21, "Tiradentes"],
    [5, 1, "Dia do Trabalho"],
    [9, 7, "Independência do Brasil"],
    [10, 12, "Nossa Sra. Aparecida"],
    [11, 2, "Finados"],
    [11, 15, "Proclamação da República"],
    [11, 20, "Consciência Negra"],
    [12, 25, "Natal"],
  ];

  for (const [m, d, name] of fixed) {
    holidays[`${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`] = name;
  }

  // Feriados móveis baseados na Páscoa
  const easter = getEaster(year);
  holidays[toKey(addDays(easter, -48))] = "Segunda-feira de Carnaval";
  holidays[toKey(addDays(easter, -47))] = "Carnaval";
  holidays[toKey(addDays(easter, -46))] = "Quarta-feira de Cinzas";
  holidays[toKey(addDays(easter, -2))] = "Sexta-feira Santa";
  holidays[toKey(easter)] = "Páscoa";
  holidays[toKey(addDays(easter, 60))] = "Corpus Christi";

  return holidays;
}

const MONTHS_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default function HeaderCalendar() {
  const today = new Date();
  const [current, setCurrent] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [open, setOpen] = useState(false);

  // Pré-computa feriados para o ano atual e adjacentes
  const holidays = useMemo(() => ({
    ...getHolidays(current.year - 1),
    ...getHolidays(current.year),
    ...getHolidays(current.year + 1),
  }), [current.year]);

  // Células do calendário (null = dia vazio antes do primeiro dia)
  const cells = useMemo(() => {
    const firstDow = new Date(current.year, current.month, 1).getDay();
    const totalDays = new Date(current.year, current.month + 1, 0).getDate();
    const arr: (number | null)[] = Array(firstDow).fill(null);
    for (let d = 1; d <= totalDays; d++) arr.push(d);
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [current]);

  const prevMonth = () =>
    setCurrent(c => c.month === 0 ? { year: c.year - 1, month: 11 } : { ...c, month: c.month - 1 });

  const nextMonth = () =>
    setCurrent(c => c.month === 11 ? { year: c.year + 1, month: 0 } : { ...c, month: c.month + 1 });

  const goToday = () =>
    setCurrent({ year: today.getFullYear(), month: today.getMonth() });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
          title="Calendário"
        >
          <CalendarDays className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0 bg-[#f9f6f0] border border-[rgba(169,171,174,0.35)] shadow-xl"
        align="end"
      >
        {/* Cabeçalho do mês */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-[rgba(169,171,174,0.25)]">
          <button
            onClick={prevMonth}
            className="p-1 rounded hover:bg-[rgba(121,22,20,0.08)] transition-colors text-muted-foreground hover:text-primary"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={goToday}
            className="text-[12px] font-accent uppercase tracking-wider text-foreground hover:text-primary transition-colors select-none"
            title="Ir para hoje"
          >
            {MONTHS_PT[current.month]} {current.year}
          </button>
          <button
            onClick={nextMonth}
            className="p-1 rounded hover:bg-[rgba(121,22,20,0.08)] transition-colors text-muted-foreground hover:text-primary"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Cabeçalho dos dias da semana */}
        <div className="grid grid-cols-7 px-2 pt-2.5 pb-1">
          {WEEKDAYS.map((wd, i) => (
            <div
              key={wd}
              className={`text-center text-[10px] font-accent uppercase tracking-wider ${
                i === 0 || i === 6 ? "text-[#791614]/60" : "text-muted-foreground"
              }`}
            >
              {wd}
            </div>
          ))}
        </div>

        {/* Grade de dias */}
        <TooltipProvider delayDuration={150}>
          <div className="grid grid-cols-7 px-2 pb-3 gap-y-0.5">
            {cells.map((day, idx) => {
              if (!day) return <div key={`e-${idx}`} />;

              const isToday =
                day === today.getDate() &&
                current.month === today.getMonth() &&
                current.year === today.getFullYear();

              const key = `${current.year}-${String(current.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const holiday = holidays[key];
              const colInWeek = idx % 7; // 0=Dom, 6=Sáb
              const isWeekend = colInWeek === 0 || colInWeek === 6;

              const cell = (
                <div
                  key={key}
                  className={[
                    "relative flex items-center justify-center h-7 w-full rounded text-[12px] font-body cursor-default",
                    isToday
                      ? "bg-[#791614] text-white font-semibold shadow-sm"
                      : holiday
                      ? "bg-[rgba(215,179,105,0.18)] text-[#791614] font-medium cursor-help"
                      : isWeekend
                      ? "text-[#791614]/65 hover:bg-[rgba(121,22,20,0.06)]"
                      : "text-foreground hover:bg-[rgba(121,22,20,0.06)]",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {day}
                  {/* Ponto dourado indicador de feriado */}
                  {holiday && !isToday && (
                    <span className="absolute bottom-[3px] left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#d7b369]" />
                  )}
                </div>
              );

              if (holiday) {
                return (
                  <Tooltip key={key}>
                    <TooltipTrigger asChild>{cell}</TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="text-xs bg-[#2D2824] text-[#f9f6f0] border border-[rgba(215,179,105,0.4)] max-w-[200px]"
                    >
                      {holiday}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return cell;
            })}
          </div>
        </TooltipProvider>

        {/* Legenda */}
        <div className="flex items-center gap-4 px-3 py-2 border-t border-[rgba(169,171,174,0.20)]">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#d7b369] shrink-0" />
            <span className="text-[10px] text-muted-foreground">Feriado</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-[#791614] inline-flex items-center justify-center text-[9px] text-white leading-none shrink-0">
              H
            </span>
            <span className="text-[10px] text-muted-foreground">Hoje</span>
          </div>
          <button
            onClick={goToday}
            className="ml-auto text-[10px] text-[#791614] hover:underline font-medium"
          >
            Ir para hoje
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
